<template>
    <!-- Partner Portal: enable buttonless for this custom CTA (https://docs.faslet.net/docs/advanced/custom-button-styling/). Custom button is rendered in AddToCart via provide/inject. -->
    <div class="faslet-integration">
        <FasletFitBar ref="fasletFitBarRef" />
        <!-- Do not use `hidden`/display:none — Faslet may not run onButtonShow until the host is connected. -->
        <div id="faslet-app-mount" class="hidden min-h-0 w-full" />
    </div>
</template>

<script setup lang="ts">
    import { createWidget } from '@faslet/widget';
    import FasletFitBar from './FasletFitBar.vue';

    type ShopwareChild = {
        id: string;
        productNumber: string;
        stock?: number;
        availableStock?: number;
        optionIds?: readonly string[];
        calculatedPrice?: { totalPrice?: number };
        cover?: { media?: { url?: string } };
    };

    type ConfiguratorGroup = {
        translated?: { name?: string };
        name?: string;
        options?: Array<{
            id: string;
            translated?: { name?: string };
            name?: string;
        }>;
    };

    const runtimeConfig = useRuntimeConfig();
    const fasletCustomButtonTargetRef = useFasletCustomButtonTarget();

    const props = defineProps({
        product: {
            type: Object,
            required: true,
        },
        /** Shopware `configurator` (property groups); used to resolve size/color labels from variant `optionIds`. */
        variants: {
            type: Object,
            required: true,
        },
        addToCart: {
            type: Function,
            required: true,
        },
    });

    const fasletFitBarRef = ref<InstanceType<typeof FasletFitBar> | null>(null);

    function buildOptionLookup(configurator: ConfiguratorGroup[] | undefined): Map<string, { groupName: string; optionName: string }> {
        const map = new Map<string, { groupName: string; optionName: string }>();
        if (!Array.isArray(configurator)) {
            return map;
        }
        for (const group of configurator) {
            const groupName = group.translated?.name ?? group.name ?? '';
            for (const opt of group.options ?? []) {
                map.set(opt.id, {
                    groupName,
                    optionName: opt.translated?.name ?? opt.name ?? '',
                });
            }
        }
        return map;
    }

    function sizeAndColorFromOptionIds(
        optionIds: readonly string[] | undefined,
        lookup: Map<string, { groupName: string; optionName: string }>,
    ): { sizeLabel: string; colorId: string } {
        const metas = (optionIds ?? [])
            .map(oid => {
                const m = lookup.get(oid);
                return m ? { optionId: oid, ...m } : null;
            })
            .filter(Boolean) as Array<{ optionId: string; groupName: string; optionName: string }>;
        const maat = metas.find(m => m.groupName === 'Maat');
        const kleur = metas.find(m => m.groupName === 'Kleur');
        return {
            sizeLabel:
                maat?.optionName ||
                metas
                    .map(m => m.optionName)
                    .filter(Boolean)
                    .join(' / '),
            colorId: kleur?.optionId ?? '',
        };
    }

    /**
     * When variants carry a `color` id, Faslet expects a matching `colors` list (id + display name).
     * Omitting it while `color` is set can break correlation with return/fit data (e.g. neutral `onFitRating`).
     * @see https://docs.faslet.net/docs/widget/other — “Variants, Sizes, Colors and Add to Cart”
     */
    function appendFasletColors(widget: ReturnType<typeof createWidget>, configurator: ConfiguratorGroup[] | undefined): void {
        if (!Array.isArray(configurator)) {
            return;
        }
        const colorGroup = configurator.find(g => (g.translated?.name ?? g.name) === 'Kleur');
        for (const opt of colorGroup?.options ?? []) {
            const label = opt.translated?.name ?? opt.name ?? '';
            if (opt.id) {
                widget.addColor(opt.id, label);
            }
        }
    }

    function productUsesConfiguratorColors(
        product: Record<string, any>,
        configurator: ConfiguratorGroup[] | undefined,
    ): boolean {
        const lookup = buildOptionLookup(configurator);
        const children = product.children as ShopwareChild[] | undefined;
        if (!Array.isArray(children) || children.length === 0) {
            return false;
        }
        return children.some(ch => Boolean(sizeAndColorFromOptionIds(ch.optionIds, lookup).colorId));
    }

    /** Shopware color option id for the PDP being shown (single-color URL or all children share one Kleur). */
    function currentDisplayedColorOptionId(
        product: Record<string, any>,
        configurator: ConfiguratorGroup[] | undefined,
    ): string {
        const lookup = buildOptionLookup(configurator);
        const children = product.children as ShopwareChild[] | undefined;
        if (Array.isArray(children) && children.length > 0) {
            const ids = children
                .map(ch => sizeAndColorFromOptionIds(ch.optionIds, lookup).colorId)
                .filter((id): id is string => Boolean(id));
            if (ids.length > 0 && ids.every(id => id === ids[0])) {
                return ids[0]!;
            }
        }
        const propsList = product.properties as
            | Array<{ id?: string; group?: { translated?: { name?: string }; name?: string } }>
            | undefined;
        const kleur = propsList?.find(
            p => p.group?.translated?.name === 'Kleur' || p.group?.name === 'Kleur',
        );
        return kleur?.id ?? '';
    }

    function syncFasletSelectColor(product: Record<string, any>, configurator: ConfiguratorGroup[] | undefined): void {
        const colorId = currentDisplayedColorOptionId(product, configurator);
        if (!colorId) {
            return;
        }
        const w = window as Window & { _faslet?: { selectColor?: (id: string) => unknown } };
        w._faslet?.selectColor?.(colorId);
    }

    /** `selectColor` is attached by Faslet’s script after load; retry covers deferred script + client navigations. */
    function scheduleFasletColorSync(product: Record<string, any>, configurator: ConfiguratorGroup[] | undefined): void {
        const run = () => syncFasletSelectColor(product, configurator);
        queueMicrotask(run);
        const script = document.querySelector('#faslet-script-tag');
        script?.addEventListener('load', run, { once: true });
        window.setTimeout(run, 400);
        window.setTimeout(run, 2000);
    }

    function appendFasletVariants(
        widget: ReturnType<typeof createWidget>,
        product: Record<string, any>,
        configurator: ConfiguratorGroup[] | undefined,
    ): void {
        const lookup = buildOptionLookup(configurator);
        const parentCover = product.cover?.media?.url ?? '';
        const children = product.children as ShopwareChild[] | undefined;

        if (Array.isArray(children) && children.length > 0) {
            for (const child of children) {
                const { sizeLabel, colorId } = sizeAndColorFromOptionIds(child.optionIds, lookup);
                const label = sizeLabel || child.productNumber;
                const stock = child.stock ?? child.availableStock ?? 0;
                const price = child.calculatedPrice?.totalPrice ?? 0;
                const imageUrl = child.cover?.media?.url || parentCover;
                widget.addVariant(child.id, label, Number(stock) > 0, child.productNumber, colorId, price, imageUrl);
            }
            return;
        }

        const stock = product.stock ?? product.availableStock ?? 0;
        const price = product.calculatedPrice?.totalPrice ?? 0;

        widget.addVariant(
            product.id,
            product.productNumber,
            Number(stock) > 0,
            product.productNumber,
            '',
            price,
            parentCover,
        );
    }

    onMounted(() => {
        const productImageUrl = props.product.cover?.media?.url;
        if (!productImageUrl) {
            console.warn('Faslet widget: Product image URL is missing, skipping widget initialization');
            return;
        }

        const configurator = props.variants as ConfiguratorGroup[] | undefined;

        const widget = createWidget(runtimeConfig.public.fasletId)
            .withBrand(props.product.manufacturer.translated.name)
            .withProductId(props.product.productNumber)
            .withProductImage(productImageUrl)
            .withProductName(props.product.translated.name)
            .withAddToCart(props.addToCart as (variantId: string) => Promise<unknown>)
            .withOnButtonShow(() => {
                fasletCustomButtonTargetRef?.value?.handleButtonShow();
            })
            .withOnButtonHidden(() => {
                fasletCustomButtonTargetRef?.value?.handleButtonHidden();
            })
            .withOnResult((result, resultType) => {
                fasletCustomButtonTargetRef?.value?.handleResult(result, resultType);
            })
            .withOnDataChanged(data => {
                fasletCustomButtonTargetRef?.value?.handleDataChanged(data);
            });

        if (productUsesConfiguratorColors(props.product, configurator)) {
            appendFasletColors(widget, configurator);
        }
        appendFasletVariants(widget, props.product, configurator);

        try {
            widget.injectScriptTag();
            widget.addToDom('#faslet-app-mount');
            fasletFitBarRef.value?.attachFitRatingCallback();
            scheduleFasletColorSync(props.product, configurator);
        } catch (e) {
            console.warn('Faslet widget: initialization failed', e);
        }
    });
</script>
